import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createSlots as _createSlots, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex items-center gap-2" };
const _hoisted_2 = ["id"];
const _hoisted_3 = { class: "flex flex-col gap-5" };
const _hoisted_4 = { class: "flex flex-col" };
const _hoisted_5 = ["aria-label"];
const _hoisted_6 = { class: "font-bold" };
const _hoisted_7 = ["id"];
const _hoisted_8 = {
    key: 1,
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
const _hoisted_9 = { class: "flex w-full items-center justify-between" };
const _hoisted_10 = { class: "flex items-center gap-4" };
import { computed, ref, watch, onBeforeUnmount, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Checkbox, Modal, Popover, TextInput, ToggleInput } from '@eudonet/opal';
import { useAppToast } from '@/shared/composables/useAppToast';
import { resolveApiErrorMessage } from '@/infrastructure/services/ApiErrorService';
import { logger } from '@/infrastructure/services/LoggerService';
import { buildEmailErrorOverrides } from './channelErrorOverrides';
import { isValidEmail } from '@/shared/utils/validation';
import { DEFERRED_SUBMIT } from './submitDeferral';
// OPHUB caps the channel value at 255 chars — block submission past it client-side (a hard cap, not
// a format warning) so the user gets immediate feedback instead of a server-side rejection.
const EMAIL_MAX_LENGTH = 255;
const DUPLICATE_DEBOUNCE_MS = 300;
export default /*@__PURE__*/ _defineComponent({
    __name: 'EditEmailModal',
    props: /*@__PURE__*/ _mergeModels({
        initialValue: {},
        initialIsPrimary: { type: Boolean },
        submit: {},
        i18nPrefix: {},
        fieldI18nPrefix: {},
        errorOverrides: { default: () => ({}) },
        checkAlreadyUsed: { type: Function, default: undefined },
        sharedEmailsEnabled: { type: Boolean, default: false },
        sharedEmailSettingPending: { type: Boolean, default: false }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["updated", "delete"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Reusable "Modifier l'email" modal (OP-5609 / US-13).
         *
         * Mirrors {@link AddEmailModal} in look & feel but restricted to the editable fields of an existing
         * channel: the value and the primary flag. Context (Personal/Professional) and the organisation
         * anchor are NOT editable (out of scope — delete + recreate via OP-5603), so they are preserved
         * server-side by the BFF read-merge-write passthrough and never shown here.
         *
         * Owner-agnostic: the persistence call (`submit`) and the i18n namespaces are injected. `i18nPrefix`
         * resolves the edit-only keys (`title`, `delete`, `saveSuccess`, `errors.serverError`); `fieldI18nPrefix`
         * reuses the existing "Ajouter un email" field labels (`email.*`, `primary`, `actions.*`, `validation.*`)
         * so the two modals stay visually and textually consistent without duplicating keys.
         */
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t } = useI18n();
        const tkOwn = (key) => t(`${props.i18nPrefix}.${key}`);
        const tkField = (key) => t(`${props.fieldI18nPrefix}.${key}`);
        const { toastSuccess } = useAppToast();
        const titleId = useId();
        const duplicateHelperId = useId();
        const email = ref(props.initialValue);
        const isPrimary = ref(props.initialIsPrimary);
        const isShared = ref(false);
        const submitting = ref(false);
        const serverError = ref(null);
        // Display names of OTHER records already using the typed value (US-04c Cas 2).
        const associatedNames = ref([]);
        let debounceTimer;
        // Monotonic id so a slow in-flight lookup can't clobber a newer one (race guard).
        let requestId = 0;
        function clearDuplicateState() {
            associatedNames.value = [];
        }
        // Re-seed the form whenever the modal (re)opens so it always reflects the latest channel data.
        watch(open, (isOpen) => {
            if (isOpen) {
                email.value = props.initialValue;
                isPrimary.value = props.initialIsPrimary;
                isShared.value = false;
                serverError.value = null;
                clearDuplicateState();
            }
        });
        const trimmedEmail = computed(() => email.value.trim());
        // Whether the value actually changed from the channel's current one — typing the email back
        // unchanged is never a collision (the address legitimately belongs to this contact / its group).
        const valueChanged = computed(() => trimmedEmail.value !== props.initialValue.trim());
        const isEmailValid = computed(() => !!trimmedEmail.value && trimmedEmail.value.length <= EMAIL_MAX_LENGTH && isValidEmail(trimmedEmail.value));
        // Real-time duplicate detection on the NEW value (US-04c Cas 2). Only runs for a changed, valid
        // value when a predicate is injected; debounced + race-guarded like the add-email flow.
        watch(email, () => {
            if (debounceTimer)
                clearTimeout(debounceTimer);
            const current = ++requestId;
            // The share opt-in is consent for ONE specific address. Any value change invalidates it, so a
            // subsequent (different) collision re-shows the box un-ticked and re-gates Save — the user can
            // never silently share a second address on the strength of a tick made for the first.
            isShared.value = false;
            if (!props.checkAlreadyUsed || !valueChanged.value || !isEmailValid.value) {
                clearDuplicateState();
                return;
            }
            debounceTimer = setTimeout(async () => {
                try {
                    const names = await props.checkAlreadyUsed(trimmedEmail.value);
                    if (current !== requestId)
                        return; // a newer keystroke superseded this lookup
                    associatedNames.value = names;
                }
                catch (error) {
                    if (current !== requestId)
                        return;
                    clearDuplicateState();
                    logger.error('Failed to check email duplicates', error);
                }
            }, DUPLICATE_DEBOUNCE_MS);
        });
        onBeforeUnmount(() => {
            if (debounceTimer)
                clearTimeout(debounceTimer);
        });
        const emailAlreadyUsed = computed(() => associatedNames.value.length > 0);
        // The shared opt-in box shows only when the new value collides AND the module setting allows it.
        const showSharedOptIn = computed(() => emailAlreadyUsed.value && props.sharedEmailsEnabled);
        // A withdrawn opt-in (setting switched off, server refusal) must not leave the group icon green.
        watch(showSharedOptIn, (offered) => { if (!offered)
            isShared.value = false; });
        const showDuplicateMessage = computed(() => emailAlreadyUsed.value && !showSharedOptIn.value && !emailError.value && !props.sharedEmailSettingPending);
        // Blocking format error (OP-6095). Empty / too long / malformed all surface an inline error and
        // gate submission via `canSubmit`.
        const emailError = computed(() => {
            if (!trimmedEmail.value)
                return '';
            if (trimmedEmail.value.length > EMAIL_MAX_LENGTH)
                return tkField('validation.emailTooLong');
            if (!isValidEmail(trimmedEmail.value))
                return tkField('validation.emailInvalid');
            return '';
        });
        // "Enregistrer" is disabled when the value is empty / over the hard length cap / malformed
        // (OP-6095 — an invalid address must not be saveable and must not trigger an API call), OR when
        // the new value collides with another contact: a collision is saveable only once sharing is
        // offered AND ticked (Cas 2, OP-8215).
        const canSubmit = computed(() => isEmailValid.value && (!emailAlreadyUsed.value || (showSharedOptIn.value && isShared.value)));
        async function handleSubmit() {
            if (!canSubmit.value)
                return;
            submitting.value = true;
            serverError.value = null;
            try {
                const result = await props.submit({
                    value: trimmedEmail.value,
                    isPrimary: isPrimary.value,
                    isShared: showSharedOptIn.value ? isShared.value : false,
                });
                if (result === DEFERRED_SUBMIT)
                    return;
                emit('updated');
                open.value = false;
                toastSuccess(`${props.i18nPrefix}.saveSuccess`);
            }
            catch (error) {
                serverError.value = resolveApiErrorMessage(error, buildEmailErrorOverrides(props.fieldI18nPrefix, props.errorOverrides), `${props.i18nPrefix}.errors.serverError`);
                logger.error('Failed to update email', error);
            }
            finally {
                submitting.value = false;
            }
        }
        function handleDelete() {
            emit('delete');
        }
        function handleCancel() {
            serverError.value = null;
            open.value = false;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": [
                    _cache[3] || (_cache[3] = ($event) => ((open).value = $event)),
                    _cache[4] || (_cache[4] = (v) => !v && handleCancel())
                ],
                size: "sm",
                "show-close-button": true,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _cache[5] || (_cache[5] = _createElementVNode("span", {
                            class: "flex h-9 items-center justify-center rounded-lg border border-(--border-border-base-soft) px-3 dark:border-(--border-border-base)",
                            "aria-hidden": "true"
                        }, [
                            _createElementVNode("i", { class: "ri-mail-line text-xl text-(--text-text-black-default) dark:text-(--text-text-white-default)" })
                        ], -1 /* CACHED */)),
                        _createElementVNode("h2", {
                            id: _unref(titleId),
                            class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                        }, _toDisplayString(tkOwn('title')), 9 /* TEXT, PROPS */, _hoisted_2)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createElementVNode("div", _hoisted_9, [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: tkField('actions.cancel'),
                            onClick: handleCancel
                        }, null, 8 /* PROPS */, ["label"]),
                        _createElementVNode("div", _hoisted_10, [
                            _createVNode(_unref(Button), {
                                variant: "error-secondary",
                                label: tkOwn('delete'),
                                "icon-left": "ri-delete-bin-line",
                                onClick: handleDelete
                            }, null, 8 /* PROPS */, ["label"]),
                            _createVNode(_unref(Button), {
                                variant: "primary",
                                label: tkField('actions.submit'),
                                disabled: !canSubmit.value || submitting.value,
                                loading: submitting.value,
                                onClick: handleSubmit
                            }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                        ])
                    ])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_unref(TextInput), {
                                modelValue: email.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((email).value = $event)),
                                type: "email",
                                label: tkField('email.label'),
                                placeholder: tkField('email.placeholder'),
                                error: emailError.value,
                                "icon-left": "ri-mail-send-line",
                                "full-width": "",
                                class: _normalizeClass(emailError.value ? '' : '-mb-4.5'),
                                "aria-describedby": showDuplicateMessage.value ? _unref(duplicateHelperId) : undefined
                            }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                                (associatedNames.value.length)
                                    ? {
                                        name: "iconRight",
                                        fn: _withCtx(() => [
                                            _createVNode(_unref(Popover), {
                                                position: "top",
                                                header: tkField('duplicate.title')
                                            }, {
                                                trigger: _withCtx(({ triggerProps }) => [
                                                    _createElementVNode("button", _mergeProps(triggerProps, {
                                                        type: "button",
                                                        class: ["flex items-center", isShared.value ? 'text-(--colors-icon-success)' : 'text-(--colors-icon-warning)'],
                                                        "aria-label": tkField('duplicate.title')
                                                    }), [...(_cache[6] || (_cache[6] = [
                                                            _createElementVNode("i", {
                                                                class: "ri-group-2-line text-base",
                                                                "aria-hidden": "true"
                                                            }, null, -1 /* CACHED */)
                                                        ]))], 16 /* FULL_PROPS */, _hoisted_5)
                                                ]),
                                                default: _withCtx(() => [
                                                    _createElementVNode("span", null, _toDisplayString(`${tkField('duplicate.associatedWith')} `), 1 /* TEXT */),
                                                    _createElementVNode("span", _hoisted_6, _toDisplayString(associatedNames.value.join(', ')), 1 /* TEXT */)
                                                ]),
                                                _: 1 /* STABLE */
                                            }, 8 /* PROPS */, ["header"])
                                        ]),
                                        key: "0"
                                    }
                                    : undefined
                            ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "label", "placeholder", "error", "class", "aria-describedby"]),
                            (showDuplicateMessage.value)
                                ? (_openBlock(), _createElementBlock("p", {
                                    key: 0,
                                    id: _unref(duplicateHelperId),
                                    "data-testid": "edit-email-duplicate-helper",
                                    class: "flex items-center gap-1 px-px text-xs text-(--text-text-error-strong) dark:text-(--text-text-error)",
                                    role: "alert"
                                }, [
                                    _cache[7] || (_cache[7] = _createElementVNode("i", {
                                        class: "ri-error-warning-fill",
                                        "aria-hidden": "true"
                                    }, null, -1 /* CACHED */)),
                                    _createElementVNode("span", null, _toDisplayString(tkField('validation.emailAlreadyUsed')), 1 /* TEXT */)
                                ], 8 /* PROPS */, _hoisted_7))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (showSharedOptIn.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                "data-testid": "edit-email-shared-optin",
                                class: _normalizeClass([
                                    'flex items-center gap-2.5 p-4 rounded-xl border w-full',
                                    isShared.value
                                        ? 'bg-(--background-background-success) border-(--border-border-success)'
                                        : 'bg-(--background-background-warning) border-(--border-border-warning-subtle)',
                                ])
                            }, [
                                _createVNode(_unref(Checkbox), {
                                    modelValue: isShared.value,
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((isShared).value = $event)),
                                    label: tkOwn('shared'),
                                    variant: "default",
                                    "label-wrap": ""
                                }, null, 8 /* PROPS */, ["modelValue", "label"])
                            ], 2 /* CLASS */))
                            : _createCommentVNode("v-if", true),
                        _createVNode(_unref(ToggleInput), {
                            modelValue: isPrimary.value,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((isPrimary).value = $event)),
                            type: "default",
                            label: tkField('primary'),
                            disabled: __props.initialIsPrimary
                        }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"]),
                        (serverError.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                _cache[8] || (_cache[8] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(serverError.value), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby"]));
        };
    }
});
